import { signedDistanceInDirectionOf } from '@geometry'
import { min } from 'lodash'
import { object, string, number, array } from 'yup'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

const pointSchema = object({
  x: number().required('X coordinate is required').defined(),
  y: number().required('Y coordinate is required').defined(),
  z: number().required('Z coordinate is required').defined(),
})

// Single unified schema with conditional validation
export const splitVerticalSlabLikeSchema = object({
  splitDirectionWRTSpan: string().required().oneOf(['parallel', 'orthogonal']),
  splitPoint: pointSchema.test({
    name: 'split-point-within-bounds',
    message: 'Projektion des Trennungspunktes muss zwischen P1 und P2 liegen',
    test: function (splitPoint) {
      const { splitDirectionWRTSpan, slabLike } = this.parent

      // Only validate for parallel splits
      if (splitDirectionWRTSpan === 'parallel') {
        if (!splitPoint || !slabLike?.shape?.points || slabLike.shape.points.length !== 4) {
          return true // Skip validation if data is not complete
        }

        const points = slabLike.shape.points
        const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
        const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
        const split = new ImmutableVector3(splitPoint.x, splitPoint.y, splitPoint.z)

        // Calculate base axis
        const baseAxis = p1.sub(p0)
        const baseAxisDirection = baseAxis.normalize()
        const baseAxisLength = p0.distanceTo(p1)

        // Calculate signed distance from p0 to splitPoint along base axis
        const distanceP0toSplitPointAlongBase = signedDistanceInDirectionOf(
          p0,
          split,
          baseAxisDirection,
        )

        // Check if split point is within bounds [0, baseAxisLength]
        return (
          distanceP0toSplitPointAlongBase >= 0 && distanceP0toSplitPointAlongBase <= baseAxisLength
        )
      } else if (splitDirectionWRTSpan === 'orthogonal') {
        if (!splitPoint || !slabLike?.shape?.points || slabLike.shape.points.length !== 4) {
          return true // Skip validation if data is not complete
        }

        const points = slabLike.shape.points
        const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
        const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
        const p2 = new ImmutableVector3(points[2].x, points[2].y, points[2].z)
        const p3 = new ImmutableVector3(points[3].x, points[3].y, points[3].z)

        const minLength = min([p0.distanceTo(p3), p1.distanceTo(p2)])

        const spanDirection = p3.sub(p0).normalize()

        const split = new ImmutableVector3(splitPoint.x, splitPoint.y, splitPoint.z)

        const distanceSplitPointAlongSpanDirection = signedDistanceInDirectionOf(
          p0,
          split,
          spanDirection,
        )
        return (
          distanceSplitPointAlongSpanDirection >= 0 &&
          distanceSplitPointAlongSpanDirection <= minLength!
        )
      }

      // Default case: if splitDirectionWRTSpan is neither parallel nor orthogonal
      return true
    },
  }),
  slabLike: object({
    guid: string().required('Guid is required').defined(),
    shape: object({
      points: array().of(pointSchema).required('Points are required').defined().length(4),
    }),
  }),
})
